import {InvalidCode, InvalidCodeDialog} from "./InvalidCodeDialog";
import * as React from "react";
import {useMemo, useState} from "react";
import {
    classNames,
    CodeKeyboardLayout,
    layout,
    PushButton,
    useActivateListener,
    useStorageItemValue,
    View,
    ViewContent,
    ViewHeader,
    ViewHeaderItem
} from "@variocube/cube-ui";
import {stylesheet} from "typestyle";
import {Help} from "./help";
import dpdLogo from "./dpd-logo.svg";
import glsLogo from "./gls-logo.png";
import upsLogo from "./ups-icon.svg";
import dhlLogo from "./dhl-logo.svg";
import wienboxLogo from "./Wienbox_Logo_klein HG_Blau.png";
import {CodeForm} from "./input/CodeForm";
import {useLocalization} from "../i18n";

interface CarrierCodeFormViewProps {
    carrier?: string;
    handleCodeVerify: (code: string) => Promise<void>;
    invalidCode?: InvalidCode;
    setInvalidCode: (code?: InvalidCode) => void;
    hideHeader?: boolean;
}

export function CarrierCodeFormView({ carrier, handleCodeVerify, invalidCode, setInvalidCode, hideHeader }: CarrierCodeFormViewProps) {
    const {t} = useLocalization();
    const [help, setHelp] = useState<boolean>(false);
    const tenantLogoUrl = useStorageItemValue<string>("tenantLogoUrl");
    const carrierLogo = useMemo(() => carrier && getCarrierLogo(carrier), [carrier]);

    useActivateListener(() => {
        setHelp(false);
    });

    if (help) {
        return (
            <Help onClose={() => setHelp(false)} />
        );
    }

    return (
        <View>
            {!hideHeader && (
                <ViewHeader>
                    <ViewHeaderItem fill>
                        {tenantLogoUrl && (
                            <img src={tenantLogoUrl} className={styles.tenantLogo}/>
                        )}
                    </ViewHeaderItem>
                    {carrierLogo && (
                        <ViewHeaderItem>
                            <img src={carrierLogo} className={styles.carrierLogo}/>
                        </ViewHeaderItem>
                    )}
                </ViewHeader>
            )}
            <ViewHeader>
                <ViewHeaderItem fill>
                    <div className={layout.horizontal}>
                        <div className={styles.helpButton}>&nbsp;</div>
                        <div className={classNames(styles.text, styles.viewTitle)}>
                            {t("EnterCodeOrScan")}
                        </div>
                        <div className={styles.helpButton}>
                            <PushButton onClick={() => setHelp(true)}>{t("Help")}</PushButton>
                        </div>
                    </div>
                </ViewHeaderItem>
            </ViewHeader>
            <ViewContent className={styles.content}>
                <CodeForm
                    onVerify={handleCodeVerify}
                    strings={t}
                    className={styles.codeForm}
                    input={(carrier === "gls" || carrier === "ups" || carrier == "dhl")
                        ? {type: "keyboard", layout: CodeKeyboardLayout}
                        : undefined
                    }
                    revealInput
                />
            </ViewContent>
            <InvalidCodeDialog invalidCode={invalidCode} onClose={() => setInvalidCode(undefined)}/>
        </View>
    );
}

const styles = stylesheet({
    carrierLogo: {
        width: "auto",
        height: "4rem",
        margin: "0.5em 1.5em",
        alignSelf: "center"
    },
    tenantLogo: {
        width: "auto",
        height: "5rem",
        margin: "0"
    },
    viewTitle: {
        flexGrow: 10
    },
    text: {
        fontSize: "1.75rem",
        textAlign: "center",
        padding: "1rem 2rem"
    },
    content: {
        display: "flex",
        alignItems: "center",
        justifyContent: "stretch",
    },
    codeForm: {
        flex: 1,
    },
    helpButton: {
        width: "4rem",
        paddingTop: "0.5rem",
        flexGrow: 4
    }
});

function getCarrierLogo(carrier: string) {
    switch (carrier) {
        case "dpd":
            return dpdLogo;
        case "gls":
            return glsLogo;
        case "ups":
            return upsLogo;
        case "dhl":
            return dhlLogo;
        case "wienbox":
            return wienboxLogo;
    }
}